/**
 * TimezoneSettingsCard — More-page card letting users override their
 * display timezone.
 *
 * Why this exists: events store one canonical UTC moment + a team
 * timezone. We render in the team's TZ by default so a coach in PT and
 * a parent traveling in ET see the same wall-clock time. But sometimes a
 * user genuinely lives in a different zone — eg a grandparent in another
 * country who wants to see their grandkid's game in their own local time
 * — and that's what this card is for.
 *
 * UX choices:
 *   • Auto-detect the browser TZ on first mount and offer "Use detected"
 *     as the friendliest default. Users almost always want this.
 *   • Show the currently-applied zone prominently with a short label
 *     (e.g. "PT") so users can tell at a glance what they're getting.
 *   • A native <select> for the override — small enough to fit the
 *     full list, big enough to bypass a custom dropdown rabbit hole.
 *   • Empty / null override means "fall back to the team's timezone";
 *     that's the recommended default and we surface it as "Match each
 *     team's timezone (default)".
 */
import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/chalkApi";
import { ChalkCard } from "./primitives";
import { TIMEZONE_OPTIONS, detectBrowserTimezone } from "../../lib/eventTime";

export default function TimezoneSettingsCard() {
  const { user, refresh } = useAuth();
  const browserTz = useMemo(() => detectBrowserTimezone(), []);
  // Empty-string value = "use team default". We carry that explicitly
  // through the select so the user can revert.
  const [value, setValue] = useState(user?.timezone || "");
  const [saving, setSaving] = useState(false);

  // Whenever the auth user changes (eg after a save) re-sync local state.
  useEffect(() => {
    setValue(user?.timezone || "");
  }, [user?.timezone]);

  // Auto-detect on very first load if the user has NOTHING set AND the
  // browser supports Intl. We do this silently (no toast) and only the
  // first time — subsequent visits respect whatever the user has set.
  useEffect(() => {
    if (user?.timezone || !browserTz) return;
    // Don't auto-save if the browser zone matches the typical default
    // we ship anyway — that'd be a noisy no-op write. Only auto-save
    // when the browser is genuinely in a different zone than the
    // team default.
    let cancelled = false;
    (async () => {
      try {
        await api.updateProfile({ timezone: browserTz });
        if (!cancelled) {
          await refresh();
        }
      } catch { /* silent — user can pick manually below */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save(next) {
    setSaving(true);
    try {
      // Empty string clears the override. Backend treats null as "fall
      // back to the team's timezone for every event render".
      await api.updateProfile({ timezone: next || "" });
      await refresh();
      toast.success(next ? "Timezone updated." : "Reset to team default.");
    } catch (e) {
      toast.error(e.message || "Couldn't save timezone.");
      // Revert local UI so the select reflects the saved state.
      setValue(user?.timezone || "");
    } finally {
      setSaving(false);
    }
  }

  function onUseDetected() {
    if (!browserTz) return;
    setValue(browserTz);
    save(browserTz);
  }

  const showDetectedCta = browserTz && browserTz !== value;
  const currentLabel = value
    ? (TIMEZONE_OPTIONS.find((o) => o.value === value)?.label || value)
    : "Match each team's timezone (default)";

  return (
    <ChalkCard data-testid="timezone-settings-card">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ marginTop: 2, color: "var(--green-mid)" }}>
          <Clock size={20} aria-hidden="true" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--chalk-board)" }}>
            Timezone
          </div>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2, lineHeight: 1.4 }}>
            How event times render across the app. Defaults to each team's
            local timezone so coaches and parents always see the same
            wall-clock time.
          </div>
          <div style={{
            marginTop: 10, fontSize: 13, color: "var(--chalk-board)",
            background: "var(--chalk-cream)", border: "0.5px solid var(--chalk-border)",
            borderRadius: 8, padding: "8px 10px",
          }} data-testid="timezone-current">
            <span style={{ color: "var(--chalk-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Currently</span>
            <div style={{ marginTop: 2 }}>{currentLabel}</div>
          </div>
          <label style={{ display: "block", marginTop: 10 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 4 }}>
              Override
            </div>
            <select
              data-testid="timezone-select"
              value={value}
              disabled={saving}
              onChange={(e) => { setValue(e.target.value); save(e.target.value); }}
              style={{
                width: "100%", borderRadius: 10, padding: "10px 12px",
                border: "0.5px solid var(--chalk-border)",
                background: "#FBF8F2", color: "var(--chalk-board)",
                fontSize: 14, outline: "none",
              }}
            >
              <option value="">Match each team's timezone (default)</option>
              {TIMEZONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              {/* If the browser detected a zone that isn't in our curated
                  list (rare), surface it so the user can still pick it. */}
              {browserTz &&
               !TIMEZONE_OPTIONS.some((o) => o.value === browserTz) && (
                <option value={browserTz}>{browserTz} (detected)</option>
              )}
            </select>
          </label>
          {showDetectedCta && (
            <button
              type="button"
              onClick={onUseDetected}
              data-testid="timezone-use-detected"
              disabled={saving}
              style={{
                marginTop: 8, padding: "6px 12px", borderRadius: 8,
                background: "transparent",
                border: "0.5px dashed var(--chalk-border)",
                color: "var(--green-mid)", fontWeight: 700, fontSize: 11,
                letterSpacing: "0.06em", textTransform: "uppercase",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              Use detected ({browserTz})
            </button>
          )}
        </div>
      </div>
    </ChalkCard>
  );
}
